import { DependencyList, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';

/**
 * Given a validation function that return error strings if something is wrong,
 * `useValidation` checks whether errors have been found and returns a `ValidationResult` that
 * can more easily be used by frontend code.
 *
 *
 * @param fn a validation function. If the validation should fail, this function should return a string or
 * a non-empty array of strings which represent error(s) to be displayed.
 *
 * @param deps Dependencies which cause the validation to be recomputed if they change. Similar to the list
 * of dependencies in `useMemo()` or `useEffect()`.
 *
 * @example
 * const [x, setX] = useState(4);
 *
 * const validation = useValidation(() => {
 *   if(x !== 5) {
 *     return 'X is not 5.';
 *   }
 * }, [x]); // => { valid: false, errors: ['X is not 5.'] }
 */
export function useValidation(fn: ValidationFn, deps: DependencyList): ValidationResult {
  const {
    t
  } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const result = useMemo(() => fn(t), [fn, t, ...deps]);
  let errors: Array<string> = [];
  if (result !== undefined) {
    errors = Array.isArray(result) ? result : [result];
  }
  return {
    valid: errors.length === 0,
    errors
  };
}

/**
 * A Validation function.
 *
 * Is passed the translation function for convenience's sake.
 */
export type ValidationFn = (t: TFunction<'time'>) => string | Array<string> | undefined;

/**
 * Result of a validation.
 */
export type ValidationResult = {
  /**
   * `true` if no errors have been reported.
   */
  valid: boolean;
  /**
   * All errors reported by validation.
   */
  errors: Array<string>;
};